import type { ValueOf } from '@evc/web-components';

export const PAGINATION_SIZES = {
  BROWSE: 50,
  DIRECTORIES: 99999,
} as const;

export const FILE_DOCUMENT_TYPE = {
  UNKNOWN: 'Unknown',
  FILE: 'File',
  FOLDER: 'Folder',
} as const;
export type FileDocumentType = ValueOf<typeof FILE_DOCUMENT_TYPE>;

export const FILE_ICON = {
  FOLDER: 'folder',
  FILE: 'file',
  ARCHIVE: 'archive',
  IMAGE: 'image',
  TEXT: 'text',
  VIDEO: 'video',
  PDF: 'pdf',
  PRESENTATION: 'presentation',
  SPREADSHEET: 'spreadsheet',
  NDE: 'nde',
  OMNISCAN_NDT: 'omniscan-ndt',
  PROBE_NDT: 'probe-ndt',
  DATA: 'data',
  SETUP: 'setup',
} as const;
export type FileIcon = ValueOf<typeof FILE_ICON>;

export const FILE_ICON_LABEL_MAP: Record<FileIcon, string> = {
  [FILE_ICON.FILE]: 'file-types.file',
  [FILE_ICON.FOLDER]: 'file-types.folder',
  [FILE_ICON.ARCHIVE]: 'file-types.archive',
  [FILE_ICON.IMAGE]: 'file-types.image',
  [FILE_ICON.TEXT]: 'file-types.text',
  [FILE_ICON.VIDEO]: 'file-types.video',
  [FILE_ICON.PDF]: 'file-types.pdf',
  [FILE_ICON.PRESENTATION]: 'file-types.presentation',
  [FILE_ICON.SPREADSHEET]: 'file-types.spreadsheet',
  [FILE_ICON.NDE]: 'file-types.nde',
  [FILE_ICON.OMNISCAN_NDT]: 'file-types.omniscan-ndt',
  [FILE_ICON.PROBE_NDT]: 'file-types.probe-ndt',
  [FILE_ICON.DATA]: 'file-types.data',
  [FILE_ICON.SETUP]: 'file-types.setup',
};

/**
 * File extensions and MIME subtypes (the part after `/`) for each file icon. Values are lowercase.
 * An icon with no types is never resolved automatically (e.g. `folder`).
 * Resolve an icon with `getFileIconFromMimeType` / `getFileIconFromPath`.
 */
export const FILE_ICON_CONTENT_TYPE_MAP: Record<FileIcon, string[]> = {
  [FILE_ICON.FILE]: [],
  [FILE_ICON.FOLDER]: [],
  [FILE_ICON.PDF]: ['pdf'],
  [FILE_ICON.SPREADSHEET]: [
    'csv',
    'xls',
    'xlsx',
    'vnd.ms-excel',
    'vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  [FILE_ICON.TEXT]: [
    'txt',
    'plain',
    'doc',
    'docx',
    'msword',
    'vnd.msword',
    'vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  [FILE_ICON.PRESENTATION]: [
    'ppt',
    'pptx',
    'vnd.openxmlformats-officedocument.presentationml.presentation',
    'vnd.ms-powerpoint',
  ],
  [FILE_ICON.IMAGE]: [
    'jpeg',
    'jpg',
    'png',
    'gif',
    'webp',
    'tiff',
    'heic',
    'svg+xml',
    'svg',
    'ai',
    'psd',
  ],
  [FILE_ICON.VIDEO]: [
    'avi',
    'mov',
    'quicktime',
    'mp4',
    'mpg',
    'mpeg',
    'webm',
    'mkv',
    'flv',
    'vob',
    'ogv',
    'ogg',
  ],
  [FILE_ICON.ARCHIVE]: ['zip', 'x-zip-compressed', 'rar', '7z', '7zip', 'x-7z-compressed'],
  [FILE_ICON.NDE]: ['nde', 'data-nde'],
  [FILE_ICON.OMNISCAN_NDT]: [],
  [FILE_ICON.PROBE_NDT]: [],
  [FILE_ICON.DATA]: ['uset', 'set', 'dat', 'odat'],
  [FILE_ICON.SETUP]: ['rdt', 'odp', 'fdp'],
};
